import type { PhotoRemovalRequest } from '@/components/photo-submissions';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Label,
  LoadingSpinner,
  PageContainer,
  Textarea,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Check, Clock, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
          <Clock className="mr-1 h-3 w-3" />
          Pending Review
        </Badge>
      );
    case 'approved':
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          <Check className="mr-1 h-3 w-3" />
          Approved
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          <X className="mr-1 h-3 w-3" />
          Rejected
        </Badge>
      );
    default:
      return null;
  }
}

function getUserName(user?: PhotoRemovalRequest['user']) {
  if (!user) return 'Unknown';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email;
}

/**
 * Removal Request detail page with approval/rejection form
 */
export function RemovalRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [adminNotes, setAdminNotes] = useState('');
  const [notesError, setNotesError] = useState('');

  const { data: request, isLoading, error, refetch } = useQuery<PhotoRemovalRequest>({
    queryKey: ['photo-removal-request', id],
    queryFn: async () => {
      const response = (await apiClient.get(`/photo-removal-requests/${id}`)) as {
        data: PhotoRemovalRequest | { data: PhotoRemovalRequest };
      };
      // Handle both wrapped and unwrapped response formats
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data) {
        return (data as { data: PhotoRemovalRequest }).data;
      }
      return data as PhotoRemovalRequest;
    },
    enabled: !!id,
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ action, notes }: { action: 'approve' | 'reject'; notes: string }) => {
      const response = (await apiClient.put(`/photo-removal-requests/${id}/review`, {
        action,
        adminNotes: notes,
      })) as { data: unknown };
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['photo-removal-requests'] });
      queryClient.invalidateQueries({ queryKey: ['photo-removal-request', id] });
      toast.success(
        variables.action === 'approve'
          ? 'Photo removal approved - the photo has been deleted'
          : 'Removal request rejected'
      );
      navigate('/photo-submissions?tab=removal-requests');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to process request');
    },
  });

  const handleAction = (action: 'approve' | 'reject') => {
    if (!adminNotes.trim()) {
      setNotesError(
        action === 'approve'
          ? 'Please provide a reason for approving this removal'
          : 'Please provide a reason for rejecting this request'
      );
      return;
    }

    reviewMutation.mutate({ action, notes: adminNotes.trim() });
  };

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">Request Not Found</h2>
          <p className="text-muted-foreground">
            The requested removal request could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error || !request) {
    return (
      <PageContainer>
        <ErrorState
          title="Failed to load removal request"
          error={error}
          action={{
            label: 'Try again',
            onClick: () => refetch(),
          }}
        />
      </PageContainer>
    );
  }

  const isPending = request.status === 'pending';

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/photo-submissions?tab=removal-requests"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Removal Requests
          </Link>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Photo Removal Request
            </h1>
            <p className="text-muted-foreground mt-1">
              Review this request to remove a user's photo from the gallery
            </p>
          </div>
          <StatusBadge status={request.status} />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left Column - Photo and Entity Info */}
          <div className="space-y-6">
            {/* Photo Card */}
            <Card>
              <CardHeader>
                <CardTitle>Photo to Remove</CardTitle>
                <CardDescription>
                  {request.photoSubmission?.entityName || 'Unknown Entity'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {request.photoSubmission?.media?.url ? (
                  <img
                    src={request.photoSubmission.media.url}
                    alt="Photo requested for removal"
                    className="w-full max-h-[400px] object-contain rounded-lg bg-muted"
                  />
                ) : (
                  <div className="h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                    Photo not available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Entity Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Entity Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">Entity Type</Label>
                    <p className="capitalize">{request.photoSubmission?.entityType || 'Unknown'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Entity Name</Label>
                    <p>
                      {request.photoSubmission?.entityType === 'attraction' ? (
                        <Link
                          to={`/attractions/${request.photoSubmission?.entityId}`}
                          className="text-primary hover:underline"
                        >
                          {request.photoSubmission?.entityName || request.photoSubmission?.entityId}
                        </Link>
                      ) : (
                        request.photoSubmission?.entityName || request.photoSubmission?.entityId || 'Unknown'
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Photo Status</Label>
                    <p className="capitalize">{request.photoSubmission?.status || 'Unknown'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Request Details and Action Form */}
          <div className="space-y-6">
            {/* Request Details Card */}
            <Card>
              <CardHeader>
                <CardTitle>Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground text-sm">Requested By</Label>
                  <p className="font-medium">{getUserName(request.user)}</p>
                  {request.user?.email && (
                    <p className="text-sm text-muted-foreground">{request.user.email}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Requested</Label>
                  <p>
                    {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                    <span className="text-muted-foreground text-sm ml-2">
                      ({new Date(request.createdAt).toLocaleString()})
                    </span>
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Reason for Removal</Label>
                  <div className="mt-1 p-3 bg-muted/50 rounded-md">
                    <p className="whitespace-pre-wrap">{request.reason}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Review History Card (if already reviewed) */}
            {!isPending && request.reviewer && (
              <Card>
                <CardHeader>
                  <CardTitle>Review Decision</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">Reviewed By</Label>
                    <p>{getUserName(request.reviewer)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Reviewed</Label>
                    <p>
                      {request.reviewedAt
                        ? formatDistanceToNow(new Date(request.reviewedAt), { addSuffix: true })
                        : 'Unknown'}
                    </p>
                  </div>
                  {request.adminNotes && (
                    <div>
                      <Label className="text-muted-foreground text-sm">Admin Notes</Label>
                      <div className="mt-1 p-3 bg-muted/50 rounded-md">
                        <p className="whitespace-pre-wrap">{request.adminNotes}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Action Form Card (only for pending) */}
            {isPending && (
              <Card>
                <CardHeader>
                  <CardTitle>Review Decision</CardTitle>
                  <CardDescription>
                    Approve to permanently delete the photo, or reject to keep it in the gallery
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="adminNotes">
                      Decision Notes <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="adminNotes"
                      placeholder="Explain your decision..."
                      value={adminNotes}
                      onChange={(e) => {
                        setAdminNotes(e.target.value);
                        if (notesError) setNotesError('');
                      }}
                      className={`mt-1 ${notesError ? 'border-destructive' : ''}`}
                      rows={4}
                    />
                    {notesError && (
                      <p className="text-sm text-destructive mt-1">{notesError}</p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      This will be recorded for audit purposes
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => handleAction('reject')}
                      disabled={reviewMutation.isPending}
                    >
                      <X className="mr-2 h-4 w-4" />
                      {reviewMutation.isPending ? 'Processing...' : 'Reject Request'}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleAction('approve')}
                      disabled={reviewMutation.isPending}
                    >
                      <Check className="mr-2 h-4 w-4" />
                      {reviewMutation.isPending ? 'Processing...' : 'Approve & Delete Photo'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
